import { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import { lockScroll, unlockScroll } from '@/utils/scrollLock';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/stores/auth-store';
import { useFamilyStore } from '@/stores/family-store';
import { X, Send, Trash2, MessageCircle, Image, Smile, Download } from 'lucide-react';
import { apiGet, apiPost, apiDelete } from '@/services/api-client';
import ConfirmationModal from '@/components/ConfirmationModal';
import { resizeToMaxSize } from '@/utils/image';
import { useQuery } from '@tanstack/react-query';
import { getNativePlatform } from '@/utils/native-platform';

export default function FamilyChatPage({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const [messages, setMessages] = useState<{ id: string; from: string; fromId?: string; text: string; ts: string; imageUrl?: string }[]>([]);
  const [text, setText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageBase64, setImageBase64] = useState<string | null>(null);
  const [fullscreenImage, setFullscreenImage] = useState<string | null>(null);
  const [isFullscreenImageLandscape, setIsFullscreenImageLandscape] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isDesktop, setIsDesktop] = useState<boolean>(() => {
    try {
      return typeof window !== 'undefined' ? window.matchMedia('(min-width: 768px)').matches : true;
    } catch {
      return true;
    }
  });
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const nativePlatform = getNativePlatform();
  const isNativeIOS = nativePlatform === 'ios';

  const handleDownloadFullscreenImage = useCallback(async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (!fullscreenImage) return;

    try {
      const response = await fetch(fullscreenImage);
      if (!response.ok) throw new Error(`Download failed: ${response.status}`);

      const blob = await response.blob();
      const mimeType = (blob.type || '').toLowerCase();
      const extension = mimeType.includes('jpeg') || mimeType.includes('jpg')
        ? 'jpg'
        : mimeType.includes('webp')
        ? 'webp'
        : mimeType.includes('gif')
        ? 'gif'
        : 'png';

      const randomPart = Math.floor(1000000000 + Math.random() * 9000000000);
      const fileName = `bild_${randomPart}.${extension}`;

      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Failed to download fullscreen image:', error);
    }
  }, [fullscreenImage]);

  // Use React Query to load initial messages (will use pre-fetched data from Dashboard if available)
  const { data: initialMessagesData, isLoading: isLoadingMessages } = useQuery({
    queryKey: ['family-chat-messages'],
    queryFn: async () => {
      const res = await apiGet<{ 
        messages: { id: string; fromName?: string; fromId?: string; fromUserId?: string; text: string; createdAt: string; imageUrl?: string }[];
        meta: { hasMore?: boolean };
      }>('/family/messages?limit=10');
      if (res.success && res.data) {
        return {
          messages: res.data.messages.map(m => ({ 
            id: m.id, 
            from: m.fromName || 'Okänd', 
            fromId: m.fromId || m.fromUserId,
            text: m.text, 
            ts: m.createdAt, 
            imageUrl: m.imageUrl 
          })),
          hasMore: res.data.meta?.hasMore || false
        };
      }
      return { messages: [], hasMore: false };
    },
    staleTime: 10 * 1000, // Consider fresh for 10 seconds
    refetchOnMount: true,
  });

  const scrollToBottom = () => {
    try {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }
    } catch (err) {
      console.warn('Failed to scroll chat to bottom', err);
    }
  };

  // Load more older messages when scrolling to top
  const loadMoreMessages = useCallback(async () => {
    if (!hasMoreMessages || isLoadingMore || messages.length === 0) return;
    
    setIsLoadingMore(true);
    const oldestMessage = messages[0]; // First message is oldest
    
    try {
      // Save current scroll position relative to bottom
      const scrollContainer = scrollRef.current;
      if (!scrollContainer) return;
      
      const oldScrollHeight = scrollContainer.scrollHeight;
      const oldScrollTop = scrollContainer.scrollTop;
      
      const res = await apiGet<{ 
        messages: { id: string; fromName?: string; fromId?: string; fromUserId?: string; text: string; createdAt: string; imageUrl?: string }[];
        meta: { hasMore?: boolean };
      }>(`/family/messages?limit=5&before=${encodeURIComponent(oldestMessage.ts)}`);
      
      if (res.success && res.data) {
        const fetched = res.data.messages.map(m => ({ 
          id: m.id, 
          from: m.fromName || 'Okänd', 
          fromId: m.fromId || m.fromUserId,
          text: m.text, 
          ts: m.createdAt, 
          imageUrl: m.imageUrl 
        }));
        
        if (fetched.length > 0) {
          // Prepend older messages, but avoid duplicates
          setMessages(prev => {
            const existingIds = new Set(prev.map(m => m.id));
            const newMessages = fetched.filter(m => !existingIds.has(m.id));
            if (newMessages.length === 0) return prev;
            return [...newMessages, ...prev];
          });
          setHasMoreMessages(res.data.meta?.hasMore || false);
          
          // Restore scroll position after DOM update
          requestAnimationFrame(() => {
            if (scrollContainer) {
              const newScrollHeight = scrollContainer.scrollHeight;
              const heightDiff = newScrollHeight - oldScrollHeight;
              scrollContainer.scrollTop = oldScrollTop + heightDiff;
            }
          });
        }
      }
    } catch (err) {
      console.error('Failed to load more messages', err);
    } finally {
      setIsLoadingMore(false);
    }
  }, [hasMoreMessages, isLoadingMore, messages]);

  // Update isDesktop when window resizes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 768px)');
    
    const handleMediaChange = (e: MediaQueryListEvent) => {
      setIsDesktop(e.matches);
    };
    
    // Modern browsers
    mediaQuery.addEventListener('change', handleMediaChange);
    
    return () => {
      mediaQuery.removeEventListener('change', handleMediaChange);
    };
  }, []);

  

  const scheduleScrollToBottom = useCallback(() => {
    try {
      scrollToBottom();
      requestAnimationFrame(() => scrollToBottom());
      setTimeout(() => scrollToBottom(), 120);
      setTimeout(() => scrollToBottom(), 350);
    } catch {
      // ignore
    }
  }, []);

  // Handle browser back button to close modal
  useEffect(() => {
    const handlePopState = () => {
      onClose();
    };
    
    // Save original state and replace it with modal state
    const originalState = window.history.state;
    window.history.replaceState({ ...originalState, familyChatModal: true }, '');
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
      // Restore original state when modal closes normally
      if (window.history.state?.familyChatModal) {
        window.history.replaceState(originalState, '');
      }
    };
  }, [onClose]);

  useLayoutEffect(() => {
    if (!isNativeIOS) return;

    const html = document.documentElement;
    const body = document.body;
    const scrollY = window.scrollY || window.pageYOffset || 0;

    body.dataset.familyChatScrollY = String(scrollY);
    html.classList.add('modal-open');
    body.style.top = `-${scrollY}px`;

    return () => {
      html.classList.remove('modal-open');
      const restoreY = parseInt(body.dataset.familyChatScrollY || '0', 10);
      body.style.top = '';
      delete body.dataset.familyChatScrollY;
      window.scrollTo(0, restoreY);
    };
  }, [isNativeIOS]);

  // Load initial messages from React Query data
  useEffect(() => {
    if (initialMessagesData && !isLoadingMessages) {
      setMessages(initialMessagesData.messages);
      setHasMoreMessages(initialMessagesData.hasMore);
      
      // scroll to bottom after render
      setTimeout(() => {
        scrollToBottom();
        // Only auto-focus on desktop — avoid opening the mobile keyboard automatically
        try {
          if (isDesktop) setTimeout(() => inputRef.current?.focus(), 150);
        } catch {
          // ignore
        }
        // notify server that chat was opened / last seen now
        try {
          const tsNow = new Date().toISOString();
          apiPost('/family/messages/last-seen', { ts: tsNow });
          try { localStorage.setItem('family-chat-lastSeen', tsNow); localStorage.removeItem('family-chat-lastSeen'); } catch { /* noop */ }
        } catch { /* noop */ }
      }, 50);
    }
  }, [initialMessagesData, isLoadingMessages, isDesktop]);

  useEffect(() => {
    // Setup scroll lock, viewport handlers, and polling
    let mounted = true;
    let pollInterval: ReturnType<typeof setInterval> | null = null;
    const previousBodyOverflow = typeof document !== 'undefined' ? document.body.style.overflow : '';
    const previousDocOverflow = typeof document !== 'undefined' ? document.documentElement.style.overflow : '';

    // Use shared scrollLock utility to prevent background scrolling while chat is open.
    let vvLocal: VisualViewport | undefined;
    let onWindowScroll: (() => void) | undefined;
    let onVvResize: ((ev?: Event) => void) | undefined;
    try {
      if (isNativeIOS) {
        try {
          document.body.style.overflow = 'hidden';
          document.documentElement.style.overflow = 'hidden';
        } catch {
          /* noop */
        }
      } else {
        try { lockScroll(); } catch { try { document.body.style.overflow = 'hidden'; } catch { /* noop */ } }
      }

      vvLocal = (window as unknown as { visualViewport?: VisualViewport }).visualViewport;
      onVvResize = () => {
        try { /* keep viewport handlers for keyboard adjustments */ } catch { /* noop */ }
      };
      if (vvLocal) {
        vvLocal.addEventListener('resize', onVvResize as EventListener);
        vvLocal.addEventListener('scroll', onVvResize as EventListener);
      }
      window.addEventListener('resize', onVvResize as EventListener);

      onWindowScroll = () => { /* no-op: visual viewport handled */ };
      window.addEventListener('scroll', onWindowScroll, { passive: true });
    } catch {
      try { document.body.style.overflow = 'hidden'; } catch { /* noop */ }
    }

    // Prevent touchmove to avoid scrolling background on iOS, but allow
    // touch scrolling when the touch originates inside the chat scroll area.
    const preventTouch = (ev: TouchEvent) => {
      try {
        const target = ev.target as Node | null;
        if (scrollRef.current && target && scrollRef.current.contains(target)) {
          // allow natural scrolling inside the chat container
          return;
        }
      } catch {
        // ignore errors and fallthrough to preventDefault
      }
      ev.preventDefault();
    };
    if (!isNativeIOS) {
      document.addEventListener('touchmove', preventTouch, { passive: false });
    }

    // Handle mobile on-screen keyboard via visualViewport when available
    const vv = (window as unknown as { visualViewport?: VisualViewport }).visualViewport;
    const prevVvHeight = { value: null as number | null };

    const onViewportChange = () => {
      try {
        if (vv && typeof vv.height === 'number') {
          const h = vv.height;
          // If height decreased significantly (keyboard likely opened), schedule scroll
          if (typeof prevVvHeight.value === 'number' && h < prevVvHeight.value - 40) {
            scheduleScrollToBottom();
          }
          prevVvHeight.value = h;
          return;
        }

        // Fallback: if visualViewport not available, rely on window.innerHeight changes
        const curr = window.innerHeight;
        if (typeof prevVvHeight.value === 'number' && curr < prevVvHeight.value - 80) {
          scheduleScrollToBottom();
        }
        prevVvHeight.value = curr;
      } catch {
        // ignore
      }
    };

    if (vv) {
      vv.addEventListener('resize', onViewportChange as EventListener);
      vv.addEventListener('scroll', onViewportChange as EventListener);
    }
    // Also listen to window resize as a fallback on some mobile browsers
    window.addEventListener('resize', onViewportChange as EventListener);

    // Poll for new messages while chat is open
    const pollFn = async () => {
      try {
        // Only fetch recent messages for polling (limit 20 to catch any new ones)
        const res = await apiGet<{ 
          messages: { id: string; fromName?: string; fromId?: string; fromUserId?: string; text: string; createdAt: string; imageUrl?: string }[];
        }>('/family/messages?limit=20');
        if (!mounted || !res.success || !res.data) return;
        const fetched = res.data.messages.map(m => ({ id: m.id, from: m.fromName || 'Okänd', fromId: m.fromId || m.fromUserId, text: m.text, ts: m.createdAt, imageUrl: m.imageUrl }));

        // Use functional update to compare and append only truly NEW messages (newer than what we have)
        let addedMessages: typeof fetched = [];
        setMessages(prev => {
          if (prev.length === 0) return prev;
          
          // Get the timestamp of the newest message we currently have
          const newestTimestamp = prev[prev.length - 1].ts;
          
          // Only add messages that are NEWER than our newest message
          const prevIds = new Set(prev.map(p => p.id));
          const toAdd = fetched.filter(f => !prevIds.has(f.id) && f.ts > newestTimestamp);
          
          if (toAdd.length === 0) return prev;
          addedMessages = toAdd;
          return [...prev, ...toAdd];
        });

        if (addedMessages.length > 0) {
          // dispatch cross-window event and localStorage ping only when there are new messages
          addedMessages.forEach(nm => {
            try { window.dispatchEvent(new CustomEvent('family-chat:new-message', { detail: nm })); } catch { /* noop */ }
            try { localStorage.setItem('family-chat-new', JSON.stringify({ id: nm.id, ts: nm.ts })); localStorage.removeItem('family-chat-new'); } catch { /* noop */ }
          });
          setTimeout(() => scrollToBottom(), 50);
          // update server-side last-seen to newest message timestamp when chat is open
            try {
              const ts = addedMessages[addedMessages.length - 1].ts;
              apiPost('/family/messages/last-seen', { ts });
              try { localStorage.setItem('family-chat-lastSeen', ts); localStorage.removeItem('family-chat-lastSeen'); } catch { /* noop */ }
            } catch { /* noop */ }
        }
      } catch {
        // ignore poll errors
      }
    };

    // Start polling immediately and then on interval
    pollFn();
    pollInterval = setInterval(pollFn, 2500);

    // Track desktop vs mobile (large screen vs small)
    let mq: MediaQueryList | null = null;
    let mqHandler: ((ev: MediaQueryListEvent) => void) | null = null;
    try {
      mq = window.matchMedia('(min-width: 768px)');
      mqHandler = (ev: MediaQueryListEvent) => setIsDesktop(ev.matches);
      if (mq.addEventListener) mq.addEventListener('change', mqHandler);
      else mq.addListener(mqHandler as unknown as EventListener);
    } catch {
      // ignore
    }

    return () => {
      mounted = false;

      // Unlock shared scroll lock so underlying page becomes scrollable again
      if (isNativeIOS) {
        try {
          document.body.style.overflow = previousBodyOverflow;
          document.documentElement.style.overflow = previousDocOverflow;
        } catch {
          /* noop */
        }
      } else {
        try { unlockScroll(); } catch { try { document.body.style.overflow = ''; document.documentElement.style.overflow = ''; } catch { /* noop */ } }
      }

      // remove touchmove blocker
      if (!isNativeIOS) {
        document.removeEventListener('touchmove', preventTouch as EventListener);
      }

      if (vv) {
        vv.removeEventListener('resize', onViewportChange);
        vv.removeEventListener('scroll', onViewportChange);
      }
      window.removeEventListener('resize', onViewportChange as EventListener);
      try {
        if (vvLocal && onVvResize) {
          vvLocal.removeEventListener('resize', onVvResize as EventListener);
          vvLocal.removeEventListener('scroll', onVvResize as EventListener);
        }
        if (onWindowScroll) window.removeEventListener('scroll', onWindowScroll as EventListener);
      } catch {
        // ignore
      }

      if (pollInterval) clearInterval(pollInterval);

      // remove media query listener if set
      try {
        if (mq && mqHandler) {
          if (mq.removeEventListener) mq.removeEventListener('change', mqHandler);
          else mq.removeListener(mqHandler as unknown as EventListener);
        }
      } catch {
        // ignore
      }
    };
  }, [isDesktop, isNativeIOS, scheduleScrollToBottom]);

  // Scroll event listener to load more messages when reaching top
  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer) return;

    const handleScroll = () => {
      // Check if we're near the top (within 100px)
      if (scrollContainer.scrollTop < 100 && hasMoreMessages && !isLoadingMore) {
        loadMoreMessages();
      }
    };

    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll);
    };
  }, [hasMoreMessages, isLoadingMore, loadMoreMessages]);

  const send = () => {
    const trimmed = text.trim().slice(0, 500);
    if (!trimmed && !imageFile) return;
    if (isSending) return; // Prevent double-send
    
    setIsSending(true);
    (async () => {
      try {
        let imageUrl: string | undefined;
        let imageBlobName: string | undefined;

        // Upload image if present
        if (imageFile) {
          const data = imageBase64 ?? await resizeToMaxSize(imageFile, 2 * 1024 * 1024, 800);
          const uploadRes = await apiPost('/family/messages/images', {
            filename: imageFile.name,
            contentType: 'image/jpeg',
            data
          });
          if (uploadRes.success && uploadRes.data) {
            const uploadData = uploadRes.data as { url: string; blobName: string };
            imageUrl = uploadData.url;
            imageBlobName = uploadData.blobName;
          }
        }

        const payload = { text: trimmed || ' ', imageUrl, imageBlobName };
        const res = await apiPost('/family/messages', payload);
        if (res.success && res.data) {
          const created = res.data as { id: string; fromName: string; fromId?: string; fromUserId?: string; text: string; createdAt: string; imageUrl?: string };
          const msg = { id: created.id, from: created.fromName, fromId: created.fromId || created.fromUserId, text: created.text, ts: created.createdAt, imageUrl: created.imageUrl };
          // Add message only if it doesn't already exist (avoid duplicates from race conditions)
          setMessages((s) => {
            if (s.some(m => m.id === msg.id)) return s;
            return [...s, msg];
          });
          setText('');
          setImageFile(null);
          setImagePreview(null);
          setImageBase64(null);
          // Save last-seen timestamp for unread tracking and notify server
          try { localStorage.setItem('family-chat-lastSeen', msg.ts); localStorage.removeItem('family-chat-lastSeen'); } catch { /* noop */ }
          // update server-side last-seen when we send a message
          try { apiPost('/family/messages/last-seen', { ts: msg.ts }); } catch { /* noop */ }
          // Focus back to input
          inputRef.current?.focus();
          // Scroll to bottom
          setTimeout(() => scrollToBottom(), 50);
        }
      } catch (err) {
        console.error('Failed to send message', err);
        alert('Misslyckades skicka meddelande');
      } finally {
        setIsSending(false);
      }
    })();
  };

  const sendQuickEmoji = (emoji: string) => {
    if (isSending) return; // Prevent double-send
    const trimmed = emoji.slice(0, 500);
    setIsSending(true);
    (async () => {
      try {
        const payload = { text: trimmed };
        const res = await apiPost('/family/messages', payload);
        if (res.success && res.data) {
          const created = res.data as { id: string; fromName: string; fromId?: string; fromUserId?: string; text: string; createdAt: string };
          const msg = { id: created.id, from: created.fromName, fromId: created.fromId || created.fromUserId, text: created.text, ts: created.createdAt };
          // Add message only if it doesn't already exist (avoid duplicates from race conditions)
          setMessages((s) => {
            if (s.some(m => m.id === msg.id)) return s;
            return [...s, msg];
          });
          try { localStorage.setItem('family-chat-lastSeen', msg.ts); } catch { /* noop */ }
          setTimeout(() => scrollToBottom(), 50);
        }
      } finally {
        setIsSending(false);
      }
    })();
  };

  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const deleteMessage = async (id?: string) => {
    if (!id) return;
    // Optimistically remove message
    const prev = messages;
    setMessages((s) => s.filter(m => m.id !== id));
    try {
      const res = await apiDelete(`/family/messages/${encodeURIComponent(id)}`);
      if (!res.success) {
        // rollback
        setMessages(prev);
      }
    } catch {
      setMessages(prev);
    }
  };

  const onConfirmDelete = async () => {
    if (!confirmDeleteId) return;
    const id = confirmDeleteId;
    setConfirmDeleteId(null);
    await deleteMessage(id);
  };

  const onCancelDelete = () => setConfirmDeleteId(null);

  const containerStyle: React.CSSProperties = { maxHeight: 'calc(100vh - 32px)' };

  const formatTimestamp = (iso: string) => {
    try {
      const d = new Date(iso);
      const pad = (n: number) => n.toString().padStart(2, '0');
      const yyyy = d.getFullYear();
      const MM = pad(d.getMonth() + 1);
      const dd = pad(d.getDate());
      const HH = pad(d.getHours());
      const mm = pad(d.getMinutes());
      // Format without seconds
      return `${yyyy}-${MM}-${dd} ${HH}:${mm}`;
    } catch {
      return iso;
    }
  };

  const family = useFamilyStore((s) => s.family);
  const visibleMemberNames = (() => {
    if (!family) return [] as string[];

    const names = [family.ownerName, ...family.members.map((member) => member.displayName)]
      .map((name) => name?.trim())
      .filter((name): name is string => Boolean(name));

    return names.filter((name, index) => names.indexOf(name) === index);
  })();
  // color mapping: own, then member2..5
  const colorClasses = [
    // Own: gray bubble with green left border
    'bg-gray-100 text-gray-900 border-l-4 border-green-500 dark:bg-gray-800 dark:text-gray-100 dark:border-green-400',
    // Member 2: gray bubble with purple left border
    'bg-gray-100 text-gray-900 border-l-4 border-purple-500 dark:bg-gray-800 dark:text-gray-100 dark:border-purple-400',
    // Member 3: gray bubble with orange left border
    'bg-gray-100 text-gray-900 border-l-4 border-orange-500 dark:bg-gray-800 dark:text-gray-100 dark:border-orange-400',
    // Member 4: gray bubble with teal left border
    'bg-gray-100 text-gray-900 border-l-4 border-teal-500 dark:bg-gray-800 dark:text-gray-100 dark:border-teal-400',
    // Member 5: gray bubble with blue left border
    'bg-gray-100 text-gray-900 border-l-4 border-blue-500 dark:bg-gray-800 dark:text-gray-100 dark:border-blue-400',
  ];
  const getColorForMessage = (msg: { from?: string; fromId?: string }) => {
    const me = useAuthStore.getState().user;
    if (me && ((msg.fromId && msg.fromId === me.id) || (msg.from && msg.from === me.displayName))) return colorClasses[0];
    if (!family || !family.members || family.members.length === 0) return 'bg-muted/50';
    // find member index (skip owner maybe) — map by userId first then displayName
    const idx = family.members.findIndex(m => (msg.fromId && m.userId === msg.fromId) || (msg.from && m.displayName === msg.from));
    if (idx === -1) return 'bg-muted/50';
    // members array may include owner; compute display index where 0 is owner — we want own=0, others map starting at 1
    // We'll map first member after owner to colorClasses[1], etc.
    const memberOrderIndex = idx + 1; // +1 so owner->1 etc.
    const colorIndex = Math.min(memberOrderIndex, colorClasses.length - 1);
    return colorClasses[colorIndex] || 'bg-muted/50';
  };

  return (
    <div className="fixed inset-0 z-[200] bg-background/95 p-4 md:p-8">
      <div className="max-w-3xl mx-auto flex flex-col bg-card rounded-lg overflow-hidden h-full min-h-0" style={containerStyle}>
        <div className="flex items-start justify-between px-4 py-3 border-b border-border gap-4">
          <div>
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-white" />
              {t('familyChat.title', 'Familjechatt')}
            </h2>
            {visibleMemberNames.length > 0 && (
              <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground list-disc pl-4">
                {visibleMemberNames.map((name) => (
                  <li key={name}>{name}</li>
                ))}
              </ul>
            )}
          </div>
          <button onClick={onClose} className="p-2 rounded hover:bg-muted">
            <X />
          </button>
        </div>
        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto p-4 space-y-4"
          style={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-y' } as React.CSSProperties}
        >
          {isLoadingMessages ? (
            // Skeleton loader while messages are loading
            <>
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="p-3 rounded-lg bg-muted/50 space-y-2">
                    <div className="flex items-start justify-between">
                      <div className="h-4 bg-muted rounded w-24"></div>
                      <div className="h-3 bg-muted rounded w-16 ml-2"></div>
                    </div>
                    <div className="space-y-2">
                      <div className="h-3 bg-muted rounded w-full"></div>
                      <div className="h-3 bg-muted rounded w-3/4"></div>
                    </div>
                  </div>
                </div>
              ))}
            </>
          ) : (
            <>
              {/* Load more indicator at top */}
              {hasMoreMessages && (
                <div className="text-center py-2">
                  {isLoadingMore ? (
                    <div className="text-sm text-muted-foreground">Laddar äldre meddelanden...</div>
                  ) : (
                    <div className="text-xs text-muted-foreground">Scrolla upp för att ladda äldre meddelanden</div>
                  )}
                </div>
              )}
              {messages.map((m) => {
                const color = getColorForMessage(m);
                const isMe = m.from === (user?.displayName || 'Du') || (m.fromId && user && m.fromId === user.id);
                return (
                  <div key={m.id} className={`p-3 rounded-lg relative pr-10 ${isMe ? 'self-end' : 'self-start'} ${color}`}>
                    <div className="flex items-start justify-between">
                      <div className="text-sm font-medium pr-4 text-gray-800 dark:text-gray-100">{m.from}:</div>
                      <div className="text-xs text-muted-foreground ml-2">{formatTimestamp(m.ts)}</div>
                    </div>
                    <div className="mt-1 text-sm whitespace-pre-wrap">{m.text}</div>
                    {m.imageUrl && (
                      <div className="mt-2 pr-6">
                        <img 
                          src={m.imageUrl} 
                          alt="Bild" 
                          className="h-32 md:h-40 w-auto rounded-lg border border-border cursor-pointer hover:opacity-90 transition-opacity object-cover" 
                          onClick={() => setFullscreenImage(m.imageUrl!)}
                        />
                      </div>
                    )}
                    {isMe && (
                      <button
                        onClick={() => setConfirmDeleteId(m.id)}
                        className="absolute top-1 right-1 p-1 rounded hover:bg-muted/60"
                        aria-label={t('familyChat.delete', 'Ta bort')}
                        title={t('familyChat.delete', 'Ta bort')}
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </div>
        <div className="p-4 border-t border-border shrink-0">
          {/* Image preview */}
          {imagePreview && (
            <div className="mb-2">
              <img src={imagePreview} alt="Preview" className="max-w-full h-auto rounded-lg border border-border max-h-48" />
            </div>
          )}

          <div className="flex gap-2 items-end">
            <div className="flex-1 flex flex-col gap-2">
              {/* Image upload controls */}
              <div className="flex gap-2">
                <input
                  type="file"
                  accept="image/*"
                  id="family-chat-image-upload"
                  className="hidden"
                  onChange={async (e) => {
                    const f = e.target.files && e.target.files[0];
                    if (!f) {
                      setImageFile(null);
                      setImagePreview(null);
                      setImageBase64(null);
                      return;
                    }
                    setImageFile(f);
                    try {
                      const base64 = await resizeToMaxSize(f, 2 * 1024 * 1024, 800);
                      setImageBase64(base64);
                      setImagePreview(`data:image/jpeg;base64,${base64}`);
                    } catch (err) {
                      console.error('Failed to resize image:', err);
                      alert('Det gick inte att bearbeta bilden. Försök med en annan bild.');
                      setImageFile(null);
                      setImagePreview(null);
                      setImageBase64(null);
                    }
                  }}
                />
                <label htmlFor="family-chat-image-upload" className="px-2 py-1 rounded border border-border cursor-pointer hover:bg-muted inline-flex items-center gap-1.5 text-xs md:text-sm">
                  <Image className="w-3 h-3 md:w-4 md:h-4" />
                  Bild
                </label>
                {imagePreview && (
                  <button type="button" onClick={() => { setImageFile(null); setImagePreview(null); setImageBase64(null); }} className="px-2 py-1 rounded border border-border text-muted-foreground hover:bg-muted text-xs md:text-sm">Ta bort bild</button>
                )}
              </div>

              <textarea
                ref={inputRef}
                value={text}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setText(e.target.value.slice(0, 500))}
                onFocus={() => scheduleScrollToBottom()}
                onInput={() => scheduleScrollToBottom()}
                onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
                  if (isDesktop) {
                    // Desktop: Enter sends, Shift+Enter inserts newline
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      send();
                    }
                  }
                  // Mobile: Enter inserts newline (default behavior), use send button to send
                }}
                rows={3}
                maxLength={500}
                placeholder={t('familyChat.placeholder', 'Skriv ett meddelande...')}
                className="flex-1 rounded-lg border border-border px-3 py-2 text-sm resize-none min-h-[88px] overflow-y-auto"
                style={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-y' }}
              />
            </div>
            <div className="flex flex-col gap-2 relative">
              {/* Emoji picker popup */}
              {showEmojiPicker && (
                <div className="absolute bottom-full right-0 mb-2 bg-card border border-border rounded-lg shadow-lg p-2 flex gap-1">
                  {['❤️', '😀', '🤣', '😮', '😢', '😡', '👍'].map((emoji) => (
                    <button
                      key={emoji}
                      onClick={() => {
                        sendQuickEmoji(emoji);
                        setShowEmojiPicker(false);
                      }}
                      className="w-10 h-10 rounded hover:bg-muted flex items-center justify-center text-2xl transition-colors"
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              )}
              
              <button
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                aria-label="Välj emoji"
                className="w-10 h-10 rounded-lg text-white flex items-center justify-center transition-colors"
                style={{ backgroundColor: '#67724e' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#58613f'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#67724e'}
              >
                <Smile size={20} />
              </button>
              <button
                onClick={send}
                disabled={isSending}
                aria-label={t('familyChat.send', 'Skicka')}
                className="w-10 h-10 rounded-lg text-white flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: isSending ? '#888' : '#67724e' }}
                onMouseEnter={(e) => !isSending && (e.currentTarget.style.backgroundColor = '#58613f')}
                onMouseLeave={(e) => !isSending && (e.currentTarget.style.backgroundColor = '#67724e')}
              >
                <Send size={16} color="#ffffff" />
              </button>
            </div>
          </div>
        </div>
        <ConfirmationModal
          open={!!confirmDeleteId}
          title={t('familyChat.deleteConfirmTitle', 'Ta bort meddelande')}
          description={t('familyChat.deleteConfirmDesc', 'Är du säker på att du vill ta bort detta meddelande?')}
          confirmLabel={t('confirm', 'Bekräfta')}
          cancelLabel={t('cancel', 'Avbryt')}
          danger={true}
          onConfirm={onConfirmDelete}
          onCancel={onCancelDelete}
        />
      </div>

      {/* Fullscreen image modal */}
      {fullscreenImage && (
        <div 
          className="fixed inset-0 z-[300] bg-black/95 backdrop-blur-sm flex items-center justify-center p-0 md:p-4"
          onClick={() => setFullscreenImage(null)}
        >
          <div className="absolute bottom-4 right-4 md:top-4 md:bottom-auto flex items-center gap-2 z-10">
            <button
              onClick={handleDownloadFullscreenImage}
              className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
              aria-label="Spara bild"
              title="Spara bild"
            >
              <Download className="w-6 h-6 text-white" />
            </button>
            <button
              onClick={() => setFullscreenImage(null)}
              className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
              aria-label="Stäng"
            >
              <X className="w-6 h-6 text-white" />
            </button>
          </div>
          <img 
            src={fullscreenImage} 
            alt="Fullscreen" 
            onLoad={(e) => {
              const img = e.currentTarget;
              setIsFullscreenImageLandscape(img.naturalWidth > img.naturalHeight);
            }}
            style={!isDesktop && isFullscreenImageLandscape ? { 
              position: 'absolute',
              top: '50%',
              left: '50%',
              minWidth: '100vh',
              minHeight: '100vw',
              transform: 'translate(-50%, -50%) rotate(90deg)',
              transformOrigin: 'center'
            } : {}}
            className={!isDesktop && isFullscreenImageLandscape 
              ? "object-contain"
              : "max-w-full max-h-full object-contain"
            }
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
